import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { freightAPI } from '../api';
import FreightDialog from './FreightDialog';
import FreightAvailableUsersDialog from './FreightAvailableUsersDialog';

const FreightManagement = () => {
  const [freightTemplates, setFreightTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [usersModalVisible, setUsersModalVisible] = useState(false);
  const [editingUsersTemplate, setEditingUsersTemplate] = useState(null);

  useEffect(() => {
    fetchFreightTemplates();
  }, []);

  const fetchFreightTemplates = async () => {
    try {
      setLoading(true);
      const response = await freightAPI.getAll();
      // 按创建时间降序排序，最晚创建的排在最上面
      const sortedTemplates = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setFreightTemplates(sortedTemplates);
    } catch (error) {
      message.error('获取运费模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingTemplate(record);
    setModalVisible(true);
  };

  const handleCopy = (record) => {
    // 创建副本，添加" - 副本"后缀
    const copyTemplate = {
      ...record,
      id: null, // 清除ID，表示这是新建
      name: `${record.name} - 副本`
    };
    setEditingTemplate(copyTemplate);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await freightAPI.delete(id);
      message.success('删除成功');
      fetchFreightTemplates();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingTemplate(null);
  };

  const handleUsersModalClose = () => {
    setUsersModalVisible(false);
    setEditingUsersTemplate(null);
  };

  const handleSaveSuccess = () => {
    setModalVisible(false);
    setEditingTemplate(null);
    fetchFreightTemplates();
  };

  const handleEditAvailableUsers = (record) => {
    setEditingUsersTemplate(record);
    setUsersModalVisible(true);
  };

  const handleSaveUsersSuccess = () => {
    setUsersModalVisible(false);
    setEditingUsersTemplate(null);
    fetchFreightTemplates();
  };

  const formatDestinations = (data) => {
    if (!data || typeof data !== 'object') return '-';
    const destinations = Object.keys(data);
    if (destinations.length === 0) return '-';
    if (destinations.length <= 3) {
      return destinations.join(', ');
    }
    return `${destinations.slice(0, 3).join(', ')} 等${destinations.length}个地区`;
  };

  const formatFreightCurrencies = (data) => {
    if (!data || typeof data !== 'object') return '-';
    const currencies = new Set();
    
    Object.values(data).forEach(countryData => {
      if (countryData && countryData.weight) {
        Object.values(countryData.weight).forEach(weightData => {
          if (weightData && weightData.currency) {
            currencies.add(weightData.currency);
          } else {
            currencies.add('USD'); // 默认USD
          }
        });
      }
    });
    
    return currencies.size > 0 ? Array.from(currencies).join(', ') : 'USD';
  };

  const formatAvailableUsers = (record) => {
    const userIds = Array.isArray(record?.available_user_ids) ? record.available_user_ids : [];
    const users = Array.isArray(record?.available_users) ? record.available_users : [];

    if (userIds.length === 0) {
      return '所有用户';
    }

    const labels = users.map((user) => user?.username).filter(Boolean);
    if (labels.length === 0) {
      return `${userIds.length} 个指定用户`;
    }

    if (labels.length <= 3) {
      return labels.join(', ');
    }

    return `${labels.slice(0, 3).join(', ')} 等${userIds.length}个用户`;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '8%',
    },
    {
      title: '渠道名称',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      ellipsis: true,
    },
    {
      title: '覆盖地区',
      dataIndex: 'data',
      key: 'destinations',
      render: (data) => formatDestinations(data),
      ellipsis: true,
    },
    {
      title: '使用货币',
      dataIndex: 'data',
      key: 'currencies',
      render: (data) => formatFreightCurrencies(data),
      width: '12%',
    },
    {
      title: '适用用户',
      key: 'available_users',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditAvailableUsers(record)}
            style={{ padding: 0, height: 'auto' }}
          />
          <Button
            type="link"
            size="small"
            onClick={() => handleEditAvailableUsers(record)}
            style={{ padding: 0, height: 'auto' }}
          >
            {formatAvailableUsers(record)}
          </Button>
        </Space>
      ),
      width: '18%',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '15%',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '15%',
    },
    {
      title: '操作',
      key: 'action',
      width: '24%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(record)}
          >
            复制
          </Button>
          <Popconfirm
            title="确定要删除这个运费模板吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>运费模板</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          新建渠道
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={freightTemplates}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        size="middle"
      />

      <FreightDialog
        visible={modalVisible}
        editingTemplate={editingTemplate}
        onClose={handleModalClose}
        onSaveSuccess={handleSaveSuccess}
      />

      <FreightAvailableUsersDialog
        visible={usersModalVisible}
        template={editingUsersTemplate}
        onClose={handleUsersModalClose}
        onSaveSuccess={handleSaveUsersSuccess}
      />
    </div>
  );
};

export default FreightManagement;
